// ============================================
// src/components/payments/MonthlyReport.js
// Monthly payment report and analytics
// ============================================

import React, { useState } from 'react';
import { supabase } from '../../config/supabase.config';

function MonthlyReport({ payments, residents, stats, currentMonth, currentYear, onMonthChange }) {
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [sending, setSending] = useState(false);

  // Get defaulters list
  const defaulters = residents.filter(resident => {
    const hasPaid = payments.some(p => 
      p.resident_id === resident.id && p.status === 'paid'
    );
    return !hasPaid;
  });

  const handleMonthChange = () => {
    onMonthChange(selectedMonth, selectedYear);
  };

  const sendReminders = async () => {
    if (defaulters.length === 0) {
      alert('No pending payments to send reminders for!');
      return;
    }

    setSending(true);
    try {
      // Create reminder records
      const reminderPromises = defaulters.map(resident => 
        supabase
          .from('payment_reminders')
          .insert([{
            resident_id: resident.id,
            month: currentMonth,
            year: currentYear,
            reminder_type: 'whatsapp',
            sent_at: new Date().toISOString(),
            message: `Dear ${resident.name}, your maintenance payment of ₹2000 for ${currentMonth}/${currentYear} is pending. Please pay at your earliest convenience.`
          }])
      );

      await Promise.all(reminderPromises);

      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          action: `Sent payment reminders to ${defaulters.length} residents`,
          entity_type: 'payment_reminder',
          metadata: {
            month: currentMonth,
            year: currentYear,
            count: defaulters.length
          },
          created_at: new Date().toISOString()
        }]);

      alert(`Reminders sent to ${defaulters.length} residents!`);
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const exportToExcel = () => {
    // Create CSV content
    let csvContent = "Flat,Name,Phone,Amount,Status,Payment Date,Method,Transaction Ref\n";
    
    residents.forEach(resident => {
      const payment = payments.find(p => p.resident_id === resident.id);
      csvContent += `${resident.flat_number},${resident.name},${resident.phone},`;
      
      if (payment) {
        csvContent += `${payment.amount},Paid,${payment.payment_date},${payment.payment_method},${payment.transaction_ref || '-'}\n`;
      } else {
        csvContent += `2000,Pending,-,-,-\n`;
      }
    });

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Payment_Report_${currentMonth}_${currentYear}.csv`;
    a.click();
  };

  const printReport = () => {
    const printContent = `
      <html>
        <head>
          <title>Payment Report - ${currentMonth}/${currentYear}</title>
          <style>
            body { font-family: Arial, sans-serif; }
            h1 { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .summary { margin: 20px 0; }
            .paid { color: green; }
            .pending { color: red; }
          </style>
        </head>
        <body>
          <h1>Maintenance Payment Report</h1>
          <h2>${new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
          
          <div class="summary">
            <p><strong>Total Residents:</strong> ${stats.totalResidents}</p>
            <p><strong>Paid:</strong> ${stats.totalPaid}</p>
            <p><strong>Pending:</strong> ${stats.totalPending}</p>
            <p><strong>Total Collected:</strong> ₹${stats.collectedAmount}</p>
            <p><strong>Collection Rate:</strong> ${Math.round((stats.collectedAmount / stats.totalAmount) * 100)}%</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Flat</th>
                <th>Name</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Payment Date</th>
              </tr>
            </thead>
            <tbody>
              ${residents.map(resident => {
                const payment = payments.find(p => p.resident_id === resident.id);
                return `
                  <tr>
                    <td>${resident.flat_number}</td>
                    <td>${resident.name}</td>
                    <td class="${payment ? 'paid' : 'pending'}">${payment ? 'PAID' : 'PENDING'}</td>
                    <td>₹${payment ? payment.amount : '2000'}</td>
                    <td>${payment ? new Date(payment.payment_date).toLocaleDateString() : '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ color: '#333', marginBottom: '15px' }}>Monthly Payment Report</h3>
        
        {/* Month Selection */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            style={{
              padding: '10px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              background: 'white'
            }}
          >
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
              </option>
            ))}
          </select>
          
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: '10px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              background: 'white'
            }}
          >
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          
          <button
            onClick={handleMonthChange}
            style={{
              padding: '10px 20px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Load Month
          </button>
        </div>
      </div>

      {/* Report Summary */}
      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '10px',
        marginBottom: '20px'
      }}>
        <h4 style={{ marginBottom: '15px', color: '#333' }}>
          Collection Summary - {new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <div style={{ fontSize: '14px', color: '#666' }}>Total Expected</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>₹{stats.totalAmount}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#666' }}>Total Collected</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>₹{stats.collectedAmount}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#666' }}>Pending Amount</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
              ₹{stats.totalAmount - stats.collectedAmount}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#666' }}>Collection Rate</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
              {Math.round((stats.collectedAmount / stats.totalAmount) * 100)}%
            </div>
          </div>
        </div>
      </div>

      {/* Defaulters List */}
      {defaulters.length > 0 && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          padding: '20px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          <h4 style={{ marginBottom: '15px', color: '#856404' }}>
            ⚠️ Pending Payments ({defaulters.length} residents)
          </h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {defaulters.map(resident => (
              <div key={resident.id} style={{
                background: 'white',
                padding: '10px',
                borderRadius: '5px',
                border: '1px solid #ffeaa7'
              }}>
                <strong>{resident.flat_number}</strong> - {resident.name}
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  📱 {resident.phone}
                </div>
              </div>
            ))}
          </div>
          
          <button
            onClick={sendReminders}
            disabled={sending}
            style={{
              marginTop: '15px',
              padding: '10px 20px',
              background: sending ? '#ccc' : '#ffc107',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: sending ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {sending ? 'Sending...' : `📨 Send Reminders to All (${defaulters.length})`}
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={exportToExcel}
          style={{
            padding: '12px 24px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          📊 Export to Excel
        </button>
        
        <button
          onClick={printReport}
          style={{
            padding: '12px 24px',
            background: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          🖨️ Print Report
        </button>
        
        <button
          onClick={() => {
            const message = `Payment Summary for ${currentMonth}/${currentYear}:\n` +
              `Total: ${stats.totalResidents} residents\n` +
              `Paid: ${stats.totalPaid}\n` +
              `Pending: ${stats.totalPending}\n` +
              `Collected: ₹${stats.collectedAmount}/${stats.totalAmount}`;
            
            if (navigator.share) {
              navigator.share({
                title: 'Payment Report',
                text: message
              });
            } else {
              navigator.clipboard.writeText(message);
              alert('Report copied to clipboard!');
            }
          }}
          style={{
            padding: '12px 24px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          📤 Share Report
        </button>
      </div>
    </div>
  );
}

export default MonthlyReport;